import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { IApiError } from "common/interface/IApiError";
import { IDarkStoreReturn } from "common/interface/IDarkStoreReturn";
import { logoutIfNeeded } from "common/utils";

export interface State {
  returns?: IDarkStoreReturn[];
  status: "loading" | "success" | "error" | "idle";
  error?: string;
}

const initialState: State = {
  status: "idle",
};

// The exact response shape hasn't been confirmed with backend yet, so this
// normalizes whatever comes back (array, {data: [...]}, {items: [...]},
// {returns: [...]}, a single object, or null/empty) into IDarkStoreReturn[]
// instead of crashing when it isn't already a plain array.
const normalizeDarkStoreReturns = (data: any): IDarkStoreReturn[] => {
  if (Array.isArray(data)) {
    return data as IDarkStoreReturn[];
  }
  if (data && Array.isArray(data.data)) {
    return data.data as IDarkStoreReturn[];
  }
  if (data && Array.isArray(data.items)) {
    return data.items as IDarkStoreReturn[];
  }
  if (data && Array.isArray(data.returns)) {
    return data.returns as IDarkStoreReturn[];
  }
  if (data && typeof data === "object" && typeof data.id !== "undefined" && Array.isArray(data.items)) {
    return [data as IDarkStoreReturn];
  }
  return [];
};

export const getDarkStoreReturns = createAsyncThunk<IDarkStoreReturn[], { storeId: number }>(
  "dark-store-return/all",
  async ({ storeId }, thunkApi) => {
    const response = await API.get(apis.darkStoreReturn, { params: { storeId } })
      .then((success) => normalizeDarkStoreReturns(success.data))
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
      });

    return response;
  }
);

export const getDarkStoreReturnChallan = createAsyncThunk<{ id: number; challanUrl: string }, number>(
  "dark-store-return/challan",
  async (id, thunkApi) => {
    const response = await API.get(`${apis.darkStoreReturn}/${id}/challan`)
      .then((success) => {
        const data = success.data as unknown;
        const challanUrl =
          typeof data === "string"
            ? data
            : (data as { challanUrl?: string; data?: string; url?: string })?.challanUrl ??
              (data as { challanUrl?: string; data?: string; url?: string })?.data ??
              (data as { challanUrl?: string; data?: string; url?: string })?.url;
        return { id, challanUrl: challanUrl as string };
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
      });

    return response;
  }
);

const darkStoreReturnSlice = createSlice({
  name: "dark-store-return",
  initialState,
  reducers: {
    resetDarkStoreReturns: (state) => {
      state.status = "idle";
      state.error = "";
      state.returns = [];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getDarkStoreReturns.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getDarkStoreReturns.fulfilled, (state, action) => {
        state.status = "success";
        state.error = "";
        state.returns = action.payload;
      })
      .addCase(getDarkStoreReturns.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";

        if (error?.message) {
          state.error = error.message;
        }
      })
      .addCase(getDarkStoreReturnChallan.fulfilled, (state, action) => {
        const { id, challanUrl } = action.payload;
        const thisReturnIdx = state.returns?.findIndex((ret) => ret.id === id) ?? -1;
        if (thisReturnIdx > -1 && state.returns) {
          state.returns[thisReturnIdx] = {
            ...state.returns[thisReturnIdx],
            challanUrl,
          };
        }
      });
  },
});

export const { resetDarkStoreReturns } = darkStoreReturnSlice.actions;
export default darkStoreReturnSlice.reducer;
